import { Data2, useDataTypesHooks } from "../../../features/data-types"
import { useSaleBuilders } from "../builders/saleBuilders"
import { TableName } from "../../../tables/types/types"
import { useSaleActions } from "../actions/saleActions"
import { useStockService } from "../../stock"
import { useSaleLoaders } from "../loaders/saleLoaders"
import { useSaleOperationService } from "./sale-operation-service/saleOperationService"
import { usePaymentTypeHooks } from "../../administration"
import { SaleOrder } from "../state/types"

export const useSaleService = () => {
    const { generateCode } = useDataTypesHooks()
    const { getPaymentTypeYield } = usePaymentTypeHooks()

    const { buildSale } = useSaleBuilders()

    const saleLoaders = useSaleLoaders()
    
    const { buildEditedSaleOperationItems, updateReservedStock, archiveSaleOperation } = useSaleOperationService()
    const { reserveStock } = useStockService()
    const { saveSale } = useSaleActions()

    const createSale = async (
        saleData: Data2,
        itemsData: Data2[],
        keepItemsCustomerTypeYield = false
    ) => {
        const code = await generateCode(TableName.SALE_ORDERS)
        // Al mantener los precios de un presupuesto, cada ítem conserva su ajuste de lista en vez de tomar el de la lista actual
        // Los ítems de un presupuesto traen sus estadísticas de entrega: se descartan para que salgan de la cantidad final
        const items = buildEditedSaleOperationItems(itemsData, keepItemsCustomerTypeYield ? undefined : saleData.customerTypeId! as string)
        const paymentTypeYield = getPaymentTypeYield(saleData.paymentTypeId! as string)
        const sale = buildSale({
            ...saleData,
            code,
            items,
            paymentTypeYield
        })
        const savedSale = await saveSale(sale)

        for (const { productId, lotId, quantity } of items) {
            await reserveStock(productId, saleData.branchId! as string, quantity, lotId!)
        }

        return savedSale
    }

    const updateSale = async (
        saleData: Data2,
        itemsData: Data2[]
    ) => {
        const { loadSale } = saleLoaders()
        const loadedSale = await loadSale(saleData.id! as string, true)

        const items = buildEditedSaleOperationItems(itemsData, saleData.customerTypeId! as string)
        const paymentTypeYield = saleData.paymentTypeId === loadedSale!.paymentTypeId ?
            loadedSale!.paymentTypeYield :
            getPaymentTypeYield(saleData.paymentTypeId! as string)
        const sale = buildSale({
            ...loadedSale!,
            ...saleData,
            items,
            paymentTypeYield,
            internalNotes: saleData.internalNotes ?? loadedSale?.internalNotes ?? []
        })

        const savedSale = await saveSale(sale)
        await updateReservedStock(loadedSale!.items, loadedSale!.branchId, savedSale.items, savedSale.branchId)

        return savedSale
    }

    const updateSaleProps = async (
        saleId: string,
        props: Partial<SaleOrder>,
    ) => {
        const { loadSale } = saleLoaders()
        const loadedSale = await loadSale(saleId, true)

        const sale = buildSale({
            ...loadedSale!,
            ...props
        })

        return saveSale(sale)
    }

    const archiveSale = (saleId: string) => {
        return archiveSaleOperation(TableName.SALE_ORDERS, saleId)
    }

    return {
        createSale,
        updateSale,
        updateSaleProps,
        archiveSale
    }
}
